import { ref, onUnmounted } from 'vue'

import { i18n } from "@/i18n";

const t = (key: string, named?: Record<string, unknown>): string =>
	String(named ? i18n.global.t(key, named) : i18n.global.t(key));
/** Максимум файлов, добавляемых к работе за один подход */
export const MAX_FILES_PER_BATCH = 20

export type DroppedProjectFile = {
	Id: number
	Name: string
	Signed: boolean
}

export type FileValidationResult = {
	isValid: boolean
	error?: string
}

type UseFileDropOptions = {
	maxSizeMb?: number
	maxFiles?: number
	multiple?: boolean
	onFiles?: (files: File[]) => void | Promise<void>
	onProjectFile?: (parsed: DroppedProjectFile) => void | Promise<void>
	onProjectFiles?: (files: DroppedProjectFile[]) => void | Promise<void>
	onError?: (message: string) => void
}

export function useFileDrop(options: UseFileDropOptions) {
	const {
		maxSizeMb = 100,
		maxFiles = MAX_FILES_PER_BATCH,
		multiple = false,
		onFiles,
		onProjectFile,
		onProjectFiles,
		onError,
	} = options

	const isDraggingOver = ref(false)
	const canDrop = ref(false)
	const dragEnterCounter = ref(0)
	let dragLeaveTimer: ReturnType<typeof setTimeout> | null = null

	const reportError = (message: string) => {
		if (onError) onError(message)
		else console.error(message)
	}

	const validateFile = (file: File): FileValidationResult => {
		if (file.size / (1024 * 1024) > maxSizeMb) {
			return {
				isValid: false,
				error: t('file_error_too_large', { size: maxSizeMb }),
			}
		}

		if (file.name === '' || file.size === 0) {
			return {
				isValid: false,
				error: t('file_error_folders_unsupported'),
			}
		}

		return { isValid: true }
	}

	const resetDragState = () => {
		isDraggingOver.value = false
		canDrop.value = false
		dragEnterCounter.value = 0
	}

	const handleDragOver = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (!event.dataTransfer) return
		event.dataTransfer.dropEffect = 'copy'
	}

	const handleDragEnter = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (!event.dataTransfer) return

		if (dragEnterCounter.value === 0) {
			isDraggingOver.value = true
		}

		dragEnterCounter.value++
		canDrop.value = true

		if (dragLeaveTimer) {
			clearTimeout(dragLeaveTimer)
			dragLeaveTimer = null
		}
	}

	const handleDragLeave = (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		dragEnterCounter.value--

		if (dragEnterCounter.value <= 0) {
			dragLeaveTimer = setTimeout(() => {
				resetDragState()
			}, 10)
		}
	}

	const handleDrop = async (event: DragEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (dragLeaveTimer) {
			clearTimeout(dragLeaveTimer)
			dragLeaveTimer = null
		}

		resetDragState()

		if (!event.dataTransfer) {
			reportError(t('file_drop_error'))
			return
		}

		const jsonData = event.dataTransfer.getData('application/json')
		if (jsonData) {
			if (!onProjectFile && !onProjectFiles) {
				return
			}
			try {
				const parsed = JSON.parse(jsonData)
				const list = (Array.isArray(parsed) ? parsed : [parsed]).filter(
					(x: any): x is DroppedProjectFile => x && typeof x.Id === 'number'
				)
				if (list.length === 0) {
					return
				}

				let projectFiles = multiple ? list : list.slice(0, 1)
				if (projectFiles.length > maxFiles) {
					projectFiles = projectFiles.slice(0, maxFiles)
					reportError(
						t('file_batch_limit', { count: maxFiles })
					)
				}

				if (onProjectFiles) {
					await onProjectFiles(projectFiles)
				} else if (onProjectFile) {
					for (const pf of projectFiles) {
						await onProjectFile(pf)
					}
				}
			} catch (e) {
				console.error(e)
			}
			return
		}

		if (!event.dataTransfer.types.includes('Files')) {
			reportError(t('file_drop_folders_unsupported'))
			return
		}

		const incomingFiles = Array.from(event.dataTransfer.files)
		const items = Array.from(event.dataTransfer.items)
		const hasFolders = items.some(item => {
			const getAsEntry = (item as any).webkitGetAsEntry as
				| (() => { isDirectory?: boolean } | null)
				| undefined
			if (typeof getAsEntry === 'function') {
				const entry = getAsEntry.call(item)
				return !!entry && !!entry.isDirectory
			}
			const file = item.getAsFile()
			return !file || file.name === '' || file.size === 0
		})

		if (hasFolders) {
			reportError(t('file_drop_folders_unsupported_hint'))
			return
		}

		if (incomingFiles.length === 0) {
			reportError(t('file_drop_read_error'))
			return
		}

		if (!multiple && incomingFiles.length > 1) {
			reportError(t('file_drop_single_only'))
			return
		}

		const accepted: File[] = []
		for (const file of incomingFiles) {
			const validation = validateFile(file)
			if (!validation.isValid) {
				reportError(validation.error!)
				if (!multiple) return
				continue
			}
			accepted.push(file)
		}

		if (accepted.length === 0) return

		let deviceFiles = accepted
		if (deviceFiles.length > maxFiles) {
			deviceFiles = deviceFiles.slice(0, maxFiles)
			reportError(
				t('file_batch_limit', { count: maxFiles })
			)
		}

		if (onFiles) {
			await onFiles(deviceFiles)
		}
	}

	onUnmounted(() => {
		if (dragLeaveTimer) {
			clearTimeout(dragLeaveTimer)
			dragLeaveTimer = null
		}
	})

	return {
		isDraggingOver,
		canDrop,
		validateFile,
		resetDragState,
		handleDragOver,
		handleDragEnter,
		handleDragLeave,
		handleDrop,
	}
}
